import { supabase } from './supabase';

export interface Wish {
  id: string;
  title: string;
  description: string;
  category: string;
  votes: number;
  status: 'pending' | 'in-progress' | 'completed';
  created_at: string;
  user_id?: string;
  user_has_voted?: boolean;
}

export interface SubmitWishData {
  title: string;
  description: string;
  category: string;
}

export class WishingWellService {
  /**
   * Submit a new wish to the wishing well
   */
  static async submitWish(wishData: SubmitWishData): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('wishes')
        .insert({
          title: wishData.title,
          description: wishData.description,
          category: wishData.category,
          votes: 0,
          status: 'pending',
          user_id: user?.id || null
        });

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Error submitting wish:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get recent wishes from the wishing well
   */
  static async getRecentWishes(limit: number = 10): Promise<Wish[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('wishes')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching wishes:', error);
        throw error;
      }

      console.log('Fetched wishes:', data?.length, 'wishes');

      // Add user vote status to each wish
      const wishesWithVoteStatus = await Promise.all(
        (data || []).map(async (wish) => {
          let userHasVoted = false;
          
          if (user) {
            const { data: voteData } = await supabase
              .from('wish_votes')
              .select('id')
              .eq('wish_id', wish.id)
              .eq('user_id', user.id)
              .single();
            
            userHasVoted = !!voteData;
          }
          
          console.log(`Wish ${wish.id}: votes=${wish.votes}, user_has_voted=${userHasVoted}`);
          
          return {
            ...wish,
            user_has_voted: userHasVoted
          };
        })
      );

      return wishesWithVoteStatus;
    } catch (error: any) {
      console.error('Error fetching wishes:', error);
      return [];
    }
  }

  /**
   * Vote on a wish
   */
  static async voteWish(wishId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        return { success: false, error: 'Must be logged in to vote' };
      }

      // Check if user has already voted on this wish
      const { data: existingVote, error: voteCheckError } = await supabase
        .from('wish_votes')
        .select('id')
        .eq('wish_id', wishId)
        .eq('user_id', user.id)
        .single();

      if (voteCheckError && voteCheckError.code !== 'PGRST116') {
        throw voteCheckError;
      }

      if (existingVote) {
        return { success: false, error: 'You have already voted on this wish' };
      }

      // Add vote
      const { error: voteError } = await supabase
        .from('wish_votes')
        .insert({
          wish_id: wishId,
          user_id: user.id
        });

      if (voteError) throw voteError;

      // Update vote count by counting actual votes
      const { data: voteCountData, error: countError } = await supabase
        .from('wish_votes')
        .select('id', { count: 'exact' })
        .eq('wish_id', wishId);

      if (countError) {
        console.error('Error counting votes:', countError);
        throw countError;
      }

      const actualVoteCount = voteCountData?.length || 0;
      console.log('Actual vote count from wish_votes table:', actualVoteCount);

      console.log('Attempting to update vote count...');
      
      // Try direct update first (simpler approach)
      const { error: updateError } = await supabase
        .from('wishes')
        .update({ votes: actualVoteCount })
        .eq('id', wishId);

      if (updateError) {
        console.error('Direct update failed:', updateError);
        console.log('Trying RPC function as fallback...');
        
        // Fallback: Try RPC function
        const { error: rpcError } = await supabase
          .rpc('update_wish_vote_count', { 
            wish_id: wishId, 
            new_count: actualVoteCount 
          });
          
        if (rpcError) {
          console.error('RPC function also failed:', rpcError);
          throw rpcError;
        }
        
        console.log('RPC function successful - vote count updated to:', actualVoteCount);
      } else {
        console.log('Direct update successful - vote count updated to:', actualVoteCount);
      }

      return { success: true };
    } catch (error: any) {
      console.error('Error voting on wish:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get wishes by category
   */
  static async getWishesByCategory(category: string, limit: number = 20): Promise<Wish[]> {
    try {
      const { data, error } = await supabase
        .from('wishes')
        .select('*')
        .eq('category', category)
        .order('votes', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error: any) {
      console.error('Error fetching wishes by category:', error);
      return [];
    }
  }

  /**
   * Get top voted wishes
   */
  static async getTopWishes(limit: number = 10): Promise<Wish[]> {
    try {
      const { data, error } = await supabase
        .from('wishes')
        .select('*')
        .order('votes', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return data || [];
    } catch (error: any) {
      console.error('Error fetching top wishes:', error);
      return [];
    }
  }

  /**
   * Update wish status (admin function)
   */
  static async updateWishStatus(wishId: string, status: 'pending' | 'in-progress' | 'completed'): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('wishes')
        .update({ status })
        .eq('id', wishId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      console.error('Error updating wish status:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get user's submitted wishes
   */
  static async getUserWishes(userId: string): Promise<Wish[]> {
    try {
      const { data, error } = await supabase
        .from('wishes')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error: any) {
      console.error('Error fetching user wishes:', error);
      return [];
    }
  }
}
